import { CardNewMandate } from '@flywire/internal-react-elements';
import '@flywire/internal-react-elements/styles.css';

import { CreditCardIcon } from 'components/CreditCardIcon/CreditCardIcon';

import type { CardPaymentMethod } from 'models';
import { useTranslations } from 'utils';

import { CardIcon } from '@flywire/icons';
import './CreditCardListItem.scss';

type CreditCardType = CardPaymentMethod & {
  requiresCVV?: boolean;
};

type CreditCardListItemProps = {
  card?: CreditCardType;
  isSelected?: boolean;
  isCvvLoading?: boolean;
  onSelect: (creditCard?: CardPaymentMethod) => void;
};

type AddCardProps = Omit<CreditCardListItemProps, 'card' | 'isCvvLoading'>;
type CreditCardProps = Omit<CreditCardListItemProps, 'card'> & {
  card: CardPaymentMethod;
  withCvv?: boolean;
};

const AddCard = ({ isSelected = false, onSelect }: AddCardProps) => {
  const i18n = useTranslations();
  return (
    <>
      <div data-testid="addNewCard" className="CreditCardListItem-AddCard">
        <input
          type="radio"
          name="user-cc"
          id="input-other"
          checked={isSelected}
          onChange={() => onSelect()}
        />
        <label htmlFor="input-other">
          <span>{i18n.t('recurring.payment.cards.other')}</span>
        </label>
      </div>
      <div
        className="CreditCardListItem-AddCard-icons"
        data-testid="creditCardIcons"
      >
        <CardIcon className="w-10 h-auto" />
      </div>
    </>
  );
};

const CreditCard = ({
  card,
  isCvvLoading,
  isSelected,
  withCvv = true,
  onSelect,
}: CreditCardProps) => {
  const i18n = useTranslations();

  const showCvv = withCvv && isSelected;

  return (
    <div data-testid="creditCard">
      <div className="CreditCardDetails">
        <input
          type="radio"
          value={card.token}
          name="user-cc"
          id={`input-${card.token}`}
          checked={isSelected}
          onChange={() => onSelect(card)}
        />
        <div>
          <label htmlFor={`input-${card.token}`}>
            <CreditCardIcon name={card.details.scheme} />
            <span>
              {i18n.t('recurring.payment.cc.pay', {
                type: card.details.brand,
                lastFour: card.details.lastFour,
              })}
            </span>
          </label>
          {showCvv &&
            (isCvvLoading ? (
              <div className="mt-4 h-12 w-40 animate-pulse rounded-md bg-gray-200 "></div>
            ) : (
              <CardNewMandate.CVV className="CreditCardDetails-Cvv" />
            ))}
        </div>
      </div>
    </div>
  );
};

const CreditCardListItem = ({
  card,
  isCvvLoading = false,
  isSelected = false,
  onSelect,
}: CreditCardListItemProps) => {
  return (
    <li className="CreditCardListItem">
      {card ? (
        <CreditCard
          card={card}
          isCvvLoading={isCvvLoading}
          isSelected={isSelected}
          onSelect={onSelect}
          withCvv={card.requiresCVV}
        />
      ) : (
        <AddCard onSelect={onSelect} isSelected={isSelected} />
      )}
    </li>
  );
};

export { AddCard, CreditCard, CreditCardListItem, type CreditCardType };
